import React, { useEffect, useId, useState } from 'react'
import { Drawer } from '../../../../../shared/drawer/drawer'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { createKey, listResources, listRoles } from '../api'
import { KEY_EXPIRY, NEVER_EXPIRES_WARNING, customDayRange, descriptionProblem, expiresAtFor, nameProblem } from '../key-words'
import { CUSTOM_CHOICE, CUSTOM_HELP, grantsBody, grantsProblem } from '../grant-words'
import { useKeyGrantsVisible } from '../grants-access'
import { GrantsGrid } from '../grants-grid'
import { PARTNER_NEEDS_SYSTEM_KEY, keyErrorMessage, roleNeedsSystemKey, systemKeyProblem } from '../../../../../shared/partner/partner-words'
import '../api-keys.scss'

// A new API key (Task 15; ruling 17): its name, what it is for, its API
// role (only the roles content says this person may give: GET
// /api-key/roles, so a key never exceeds its creator) and its expiry. The
// create's answer, { key, secret }, goes straight to onCreated, which shows
// the secret once; nothing here keeps it.
//
// Expiry (spec 2026-10-20 §10): `expiry` is the rules this form's keys
// follow ({ choices, defaultChoice, maxYears }, key-words.js): an
// organization's key by default (KEY_EXPIRY: 30, 90 or 365 days or a date
// at most a year away, 90 by default, no Never); the LMS connection panel
// passes BOUND_EXPIRY, since core exempts bound keys.
//
// Per-resource permissions (spec 2026-10-20 §10; contract §8): EducationXR
// staff (grants-access.js: a system administrator or root in their own
// session) also get "Custom permissions" beside the presets. It opens the
// grid of the resources content offers here (GET /api-key/resources, read
// the first time Custom is chosen; AI tools telemetry only on EducationXR's
// own organization), and the key is sent with `grants` (the resources above
// No access), never a role or a system key. Everyone else sees the presets
// only, as before.
//
// Phase E (ruling E-4): an optional system key, the id space of the
// external ids the key's partner API calls push. It is set here or never
// (nothing changes it later), so a role that needs one (core's
// requires_system_key; the "API partner" preset) asks for it.
//
// The same form makes a key bound to one LMS connection (Phase D D7-8, the
// connection drawer's API keys panel): its caller gives `choices` in place
// of the roles ([{ id, name, description }], the first chosen; no roles are
// read) and what to call them (`choiceLabel`), `submit` ({ name,
// description, choice, expiresAt, systemKey } -> the answer; content's
// create by default, with the choice as the role) and `errorText` (a
// refusal in words; content's own sentence by default). A bound key never
// has a system key or custom permissions (core refuses both), so neither is
// offered when `choices` is given.
//
// A platform key (Platform -> API keys, New platform key: the platform
// organization's) opens on Custom permissions (`defaultChoice`), says what
// else its Create does (`notice`, a node or a function of the name typed:
// switching the organization's partner API access on, with its reason) and
// does it first (`beforeCreate({ name })`: its refusal is the form's error,
// the server's sentence else `beforeCreateError`, and nothing is created; what
// it answers, a sentence, is added to a refusal of the key that follows, since
// what it did stays done). `disabled` holds Create, with `notice` saying why.
const DEFAULT_INTRO = "The key acts with the role you give it, and only on the routes the partner API allows. Its secret is shown once, when it's made."

const createWithRole = ({ name, description, choice, expiresAt, systemKey }) => {
  const body = { name, description, role_id: choice, expires_at: expiresAt }
  if (systemKey) body.system_key = systemKey
  return createKey(body)
}

const contentErrorText = err => keyErrorMessage(err, "The key couldn't be created.")

export const ApiKeyCreateDrawer = ({ open, onClose, onCreated, title = 'Create API key', intro = DEFAULT_INTRO, choices, choiceLabel = 'Role', submit: send = createWithRole, errorText = contentErrorText, expiry: rules = KEY_EXPIRY, defaultChoice = null, notice, beforeCreate, beforeCreateError = "That didn't work, so the key wasn't created.", disabled = false }) => {
  const ids = { name: useId(), description: useId(), role: useId(), expiry: useId(), custom: useId(), systemKey: useId(), systemKeyHelp: useId(), grants: useId() }
  const bound = Boolean(choices)
  const offerCustom = useKeyGrantsVisible() && !bound
  const [roles, setRoles] = useState(undefined)
  const [rolesError, rolesFailure, setRolesError] = useErrorLine()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [roleID, setRoleID] = useState(defaultChoice === CUSTOM_CHOICE && offerCustom ? CUSTOM_CHOICE : null)
  const [expiry, setExpiry] = useState(rules.defaultChoice)
  const [customDay, setCustomDay] = useState('')
  const [systemKey, setSystemKey] = useState('')
  const [resources, setResources] = useState(undefined)
  const [resourcesError, resourcesFailure, setResourcesError] = useErrorLine()
  const [grants, setGrants] = useState({})
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const custom = offerCustom && roleID === CUSTOM_CHOICE

  useEffect(() => {
    if (!open) return undefined
    if (choices) {
      setRoles(choices)
      setRoleID(current => (current !== null && choices.some(c => c.id === current) ? current : choices.length ? choices[0].id : null))
      return undefined
    }
    let live = true
    listRoles().then(
      list => {
        if (!live) return
        const items = Array.isArray(list) ? list : []
        setRoles(items)
        setRoleID(current => ((current === CUSTOM_CHOICE && offerCustom) || (current !== null && items.some(r => r.id === current)) ? current : items.length ? items[0].id : null))
      },
      err => { if (live) { setRoles([]); setRolesError(serverMessage(err, "The roles you can give a key couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [open, choices, offerCustom, setRolesError])

  // The resources, read the first time Custom is chosen (never for anyone
  // who isn't offered it).
  useEffect(() => {
    if (!open || !custom || resources !== undefined) return undefined
    let live = true
    listResources().then(
      groups => { if (live) setResources(Array.isArray(groups) ? groups : []) },
      err => { if (live) { setResources([]); setResourcesError(serverMessage(err, "The resources a key can be given couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [open, custom, resources, setResourcesError])

  const partnerRole = !bound && !custom && Array.isArray(roles) && roleNeedsSystemKey(roles.find(r => r.id === roleID))
  const needsSystemKey = partnerRole && !systemKey.trim()
  const resourcesReady = Array.isArray(resources) && resources.length > 0
  // The date picker offers only the days the rule accepts (the server stays the authority).
  const dayRange = expiry === 'custom' ? customDayRange(Date.now(), rules) : null

  const submit = async e => {
    if (e) e.preventDefault()
    if (busy) return
    const problem = nameProblem(name) || descriptionProblem(description) || (roleID === null ? 'Choose a role for the key.' : null) ||
      (custom ? grantsProblem(grants) : null) ||
      (bound || custom ? null : systemKeyProblem(systemKey)) || (needsSystemKey ? PARTNER_NEEDS_SYSTEM_KEY : null)
    if (problem) return setError(problem)
    const expires = expiresAtFor(expiry, customDay, Date.now(), rules)
    if (expires.error) return setError(expires.error)
    setBusy(true)
    setError(null)
    let done = null
    if (beforeCreate) {
      try {
        done = await beforeCreate({ name: name.trim() })
      } catch (err) {
        setBusy(false)
        return setError(serverMessage(err, beforeCreateError), err)
      }
    }
    const withDone = text => (typeof done === 'string' && done ? `${text} ${done}` : text)
    try {
      let created
      if (custom) {
        created = await createKey({ name: name.trim(), description: description.trim(), expires_at: expires.value, grants: grantsBody(grants) })
      } else {
        const body = { name: name.trim(), description: description.trim(), choice: roleID, expiresAt: expires.value }
        if (!bound && systemKey.trim()) body.systemKey = systemKey.trim()
        created = await send(body)
      }
      setBusy(false)
      onCreated(created)
    } catch (err) {
      setBusy(false)
      setError(withDone(custom ? contentErrorText(err) : errorText(err)), err)
    }
  }

  const noRoles = Array.isArray(roles) && roles.length === 0
  const nothingToGive = noRoles && !offerCustom

  return (
    <Drawer open={open} title={title} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <p className="sw-hint">{intro}</p>

        <div className="ak-field">
          <label className="sw-label" htmlFor={ids.name}>Name</label>
          <input id={ids.name} className="form-control" type="text" maxLength={200} value={name} placeholder="What will use it, e.g. Nightly roster sync"
            onChange={e => setName(e.target.value)} />
        </div>

        <div className="ak-field">
          <label className="sw-label" htmlFor={ids.description}>Description (optional)</label>
          <textarea id={ids.description} className="form-control" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <div className="ak-field">
          <div className="sw-label" id={ids.role}>{choiceLabel}</div>
          {roles === undefined && <p className="sw-hint m-0">Loading roles…</p>}
          {rolesError && <p className="text-danger small m-0" role="alert">{rolesError}</p>}
          {rolesError && <ErrorReference error={rolesFailure} compact />}
          {nothingToGive && !rolesError && <p className="sw-hint m-0">There's no API role you can give a key. A key can't have permissions you don't hold yourself.</p>}
          {Array.isArray(roles) && (roles.length > 0 || offerCustom) &&
            <div className="ak-choices" role="radiogroup" aria-labelledby={ids.role}>
              {roles.map(role =>
                <label key={role.id}>
                  <input type="radio" name={`${ids.role}-role`} checked={roleID === role.id} onChange={() => setRoleID(role.id)} />
                  <span><strong>{role.name}</strong>{role.description ? <span className="sw-hint small ak-role-help">{role.description}</span> : null}</span>
                </label>)}
              {offerCustom &&
                <label>
                  <input type="radio" name={`${ids.role}-role`} checked={custom} onChange={() => setRoleID(CUSTOM_CHOICE)} />
                  <span><strong>Custom permissions</strong><span className="sw-hint small ak-role-help">{CUSTOM_HELP}</span></span>
                </label>}
            </div>}
        </div>

        {custom &&
          <div className="ak-field">
            <div className="sw-label" id={ids.grants}>Permissions</div>
            {resources === undefined && <p className="sw-hint m-0">Loading resources…</p>}
            {resourcesError && <p className="text-danger small m-0" role="alert">{resourcesError}</p>}
            {resourcesError && <ErrorReference error={resourcesFailure} compact />}
            {Array.isArray(resources) && resources.length === 0 && !resourcesError && <p className="sw-hint m-0">There's no resource a key can be given here.</p>}
            {resourcesReady && <GrantsGrid groups={resources} value={grants} onChange={setGrants} labelledBy={ids.grants} disabled={busy} />}
          </div>}

        {!bound && !custom && <div className="ak-field">
          <label className="sw-label" htmlFor={ids.systemKey}>{partnerRole ? 'System key' : 'System key (optional)'}</label>
          <input id={ids.systemKey} className="form-control sw-mono" type="text" value={systemKey} placeholder="acme-lms" aria-describedby={ids.systemKeyHelp}
            autoComplete="off" autoCapitalize="none" spellCheck={false} onChange={e => setSystemKey(e.target.value)} />
          <p className="sw-hint small mt-1 mb-0" id={ids.systemKeyHelp}>
            Names the id space of the external ids this key's partner API pushes, for example <code>acme-lms</code>. Lowercase letters, digits and dashes. It can't be changed later.
          </p>
          {needsSystemKey && <div className="sw-notice warn mt-2" role="status"><div>{PARTNER_NEEDS_SYSTEM_KEY}</div></div>}
        </div>}

        <div className="ak-field">
          <div className="sw-label" id={ids.expiry}>Expires</div>
          <div className="ak-choices ak-choices-inline" role="radiogroup" aria-labelledby={ids.expiry}>
            {rules.choices.map(choice =>
              <label key={choice.value}>
                <input type="radio" name={`${ids.expiry}-expiry`} checked={expiry === choice.value} onChange={() => setExpiry(choice.value)} />
                {choice.label}
              </label>)}
          </div>
          {expiry === 'custom' &&
            <div className="mt-2">
              <label className="sw-label" htmlFor={ids.custom}>Expires on</label>
              <input id={ids.custom} className="form-control" type="date" value={customDay} min={dayRange.min} max={dayRange.max} onChange={e => setCustomDay(e.target.value)} />
            </div>}
          {expiry === 'never' && <div className="sw-notice warn mt-2" role="status"><div>{NEVER_EXPIRES_WARNING}</div></div>}
        </div>

        {typeof notice === 'function' ? notice(name) : notice}

        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mb-2" />}

        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn sw-btn-primary" disabled={disabled || busy || nothingToGive || roles === undefined || (custom && !resourcesReady)}>Create key</button>
        </div>
      </form>
    </Drawer>
  )
}
